import React, { useState, useRef, useEffect } from 'react';
import { FiGlobe, FiMenu, FiX } from 'react-icons/fi';

/** Read the language Google Translate stored in its cookie (/en/sv → 'sv') */
function getActiveGoogleLang(): string {
  try {
    const match = document.cookie.match(/googtrans=\/[a-z-]+\/([a-z-]+)/);
    if (match && match[1] && match[1] !== 'en') return match[1];
  } catch {}
  return localStorage.getItem('siteLang') || 'en';
}

export default function Header() {
  const [lang, setLang] = useState<string>(() => getActiveGoogleLang());
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const isFaqPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/faqs');

  const desktopNavLinkClass = (active: boolean) =>
    `rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
      active
        ? 'bg-white text-[#102a43] shadow-sm'
        : 'text-[#3d5a76] hover:bg-white hover:text-[#102a43]'
    }`;

  const mobileNavLinkClass = (active: boolean) =>
    `block rounded-xl px-3 py-2 text-sm font-semibold transition-colors ${
      active
        ? 'bg-[#fff4ec] text-[#102a43]'
        : 'text-[#3d5a76] hover:bg-[#fff4ec] hover:text-[#102a43]'
    }`;

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

const langs = [
    { code: 'en', label: 'EN', display: 'English' },
    { code: 'sv', label: 'SV', display: 'Svenska' },
    { code: 'es', label: 'ES', display: 'Español' },
    { code: 'de', label: 'DE', display: 'Deutsch' },
  ];

  const changeLanguage = (code: string) => {
    setLang(code);
    setOpen(false);
    localStorage.setItem('siteLang', code);
    // Write the googtrans cookie so it survives page navigation
    const domain = window.location.hostname;
    if (code === 'en') {
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
      localStorage.removeItem('siteLang');
    } else {
      document.cookie = `googtrans=/en/${code}; path=/`;
      document.cookie = `googtrans=/en/${code}; path=/; domain=${domain}`;
      const translateWindow = window as Window & typeof globalThis & {
        loadGoogleTranslate?: () => void;
      };
      translateWindow.loadGoogleTranslate?.();
    }
    // Google Translate loads asynchronously so it cannot delay the first paint.
    // Keep trying briefly in case the visitor selects a language before it is ready.
    let attempts = 0;
    const timer = window.setInterval(() => {
      const combo = document.querySelector('.goog-te-combo') as HTMLSelectElement | null;
      attempts += 1;
      if (combo) {
        combo.value = code;
        combo.dispatchEvent(new Event('change'));
        window.clearInterval(timer);
      } else if (attempts === 40) {
        window.clearInterval(timer);
      }
    }, 100);
  };

  const navigateTo = (e: React.MouseEvent<HTMLAnchorElement>, to: string) => {
    e.preventDefault();
    if (window.location.pathname === to) return;
    window.history.pushState({}, '', to);
    window.dispatchEvent(new Event('app:navigate'));
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 px-3 pt-3 sm:px-6">
      <div className="mx-auto flex max-w-7xl items-center justify-between rounded-2xl border border-[#f2d8cc] bg-white/90 px-4 py-3 shadow-[0_20px_50px_-35px_rgba(13,27,42,0.75)] backdrop-blur-xl sm:px-6">
        <div className="flex items-center gap-6">
          <a
            href="/"
            onClick={(e) => navigateTo(e, '/')}
            className="text-xl font-bold tracking-tight sm:text-2xl"
          >
            <span className="text-[var(--brand-accent)]">Digitalbookshelf</span>
            <span className="text-[var(--brand-ink)]">care.com</span>
          </a>

          <nav className="hidden items-center gap-2 rounded-full bg-[#fff4ec] p-1 md:flex">
            <a
              href="/"
              onClick={(e) => navigateTo(e, '/')}
              className={desktopNavLinkClass(!isFaqPage)}
              aria-current={!isFaqPage ? 'page' : undefined}
            >
              HOME
            </a>
            <a
              href="/faqs"
              onClick={(e) => navigateTo(e, '/faqs')}
              className={desktopNavLinkClass(isFaqPage)}
              aria-current={isFaqPage ? 'page' : undefined}
            >
              FAQS
            </a>
          </nav>
        </div>

        <div className="flex items-center gap-2" ref={menuRef}>
          <div className="relative hidden sm:block">
            <button
              onClick={() => setOpen(v => !v)}
              className="inline-flex items-center gap-2 rounded-full border border-[#f2d8cc] bg-white px-4 py-2 text-sm font-semibold text-[var(--brand-ink)] transition-all hover:-translate-y-0.5 hover:border-[#f2643f]/60"
              aria-haspopup="true"
              aria-expanded={open}
              translate="no"
            >
              <FiGlobe className="h-4 w-4 text-[var(--brand-accent)]" />
              <span>{langs.find(l => l.code === lang)?.label ?? 'EN'}</span>
            </button>

            {open && (
              <div className="absolute right-0 z-50 mt-3 w-44 rounded-2xl border border-[#f2d8cc] bg-white py-1 shadow-xl">
                {langs.map(l => (
                  <button
                    key={l.code}
                    onClick={() => changeLanguage(l.code)}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                      lang === l.code ? 'bg-[var(--brand-accent-soft)] font-semibold text-[var(--brand-ink)]' : 'text-[var(--text-muted)] hover:bg-[var(--surface-0)]'
                    }`}
                    translate="no"
                  >
                    {l.display}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={() => setMobileOpen(v => !v)}
            className="rounded-full border border-[#f2d8cc] bg-white p-2 text-[var(--text-muted)] transition-colors hover:text-[var(--brand-accent)] md:hidden"
          >
            {mobileOpen ? <FiX className="h-5 w-5" /> : <FiMenu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav className="mx-auto mt-3 max-w-7xl space-y-2 rounded-2xl border border-[#f2d8cc] bg-white/95 p-4 shadow-[0_18px_44px_-34px_rgba(13,27,42,0.7)] md:hidden">
          <a
            href="/"
            onClick={(e) => navigateTo(e, '/')}
            className={mobileNavLinkClass(!isFaqPage)}
            aria-current={!isFaqPage ? 'page' : undefined}
          >
            HOME
          </a>
          <a
            href="/faqs"
            onClick={(e) => navigateTo(e, '/faqs')}
            className={mobileNavLinkClass(isFaqPage)}
            aria-current={isFaqPage ? 'page' : undefined}
          >
            FAQS
          </a>

          <div className="flex flex-wrap gap-2 pt-1">
            {langs.map(l => (
              <button
                key={l.code}
                onClick={() => {
                  changeLanguage(l.code);
                  setMobileOpen(false);
                }}
                className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                  lang === l.code
                    ? 'border-[var(--brand-accent)] bg-[var(--brand-accent)] text-white'
                    : 'border-[#f2d8cc] text-[var(--brand-ink)]'
                }`}
                translate="no"
              >
                {l.display}
              </button>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}
